/**
 * ConflictResolutionPage — admin/delegate view of duplicate-flagged registrations.
 */

import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { useRodeoConflicts } from '../api';
import { useBuddyGroupPermissions } from '../auth/use-buddy-group-permissions';
import { ConflictList } from '../components/ConflictList';

export default function ConflictResolutionPage() {
  const { rodeoId } = useParams<{ rodeoId: string }>();
  const navigate = useNavigate();
  const { canResolveConflicts, canDismissConflicts } = useBuddyGroupPermissions();
  const { data, isLoading, isError } = useRodeoConflicts(rodeoId!);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/rodeos/${rodeoId}/buddy-groups`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Buddy Groups
        </Button>
        <div className="text-sm text-destructive">Error loading conflicts.</div>
      </div>
    );
  }

  const conflicts = data?.conflicts ?? [];
  const pendingCount = data?.pending_count ?? 0;
  const total = data?.total ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/rodeos/${rodeoId}/buddy-groups`)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-bold">Conflict Resolution</h1>
      </div>

      {/* Summary */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>{pendingCount} pending</span>
        <span>{total} total</span>
      </div>

      {/* Conflicts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Duplicate Flags</CardTitle>
        </CardHeader>
        <CardContent>
          {conflicts.length === 0 ? (
            <EmptyState
              icon={AlertTriangle}
              title="No conflicts"
              description="No duplicate entries detected for this rodeo."
            />
          ) : (
            <ConflictList
              conflicts={conflicts}
              canDismiss={canDismissConflicts}
              canResolve={canResolveConflicts}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
